import {
  computeGarment,
  computeWithInheritance,
  measurementsFromSize,
} from './computeGarment'
import { isDerivedCode } from './derived'
import type {
  BookContent,
  GarmentContent,
  GarmentSection,
} from '../types/records'

export interface GarmentDiagnostic {
  garmentName: string
  section: GarmentSection
  status: 'ok' | 'warn' | 'error'
  pieceCount: number
  pointsExpected: number
  pointsComputed: number
  errorCount: number
  errors: Array<{
    piece: string
    point: string
    axis: 'x' | 'y'
    formula: string
    message: string
  }>
  missingMeasurements: string[]
  skipped?: string
}

const TEST_SIZES: Partial<Record<GarmentSection, string>> = {
  donna: '42',
  uomo: '50',
}

const TEST_EASE = 4

// Esegue computeGarment con misure di taglia standard e riporta lo stato.
// Serve per scoprire formule non valutabili senza dover aprire ogni capo.
export function diagnoseGarment(
  garment: GarmentContent,
  book: BookContent,
  allGarments?: GarmentContent[],
): GarmentDiagnostic {
  const section = garment.section
  const testSize = TEST_SIZES[section]
  const pointsExpected = garment.pieces.reduce((s, p) => s + p.points.length, 0)

  if (!testSize) {
    return {
      garmentName: garment.name,
      section,
      status: 'warn',
      pieceCount: garment.pieces.length,
      pointsExpected,
      pointsComputed: 0,
      errorCount: 0,
      errors: [],
      missingMeasurements: [],
      skipped: `Sezione "${section}" senza piano taglie standard`,
    }
  }

  const measurements = measurementsFromSize(testSize, section, book)
  const missing = garment.measurementsRequired.filter(
    c => !isDerivedCode(c) && typeof measurements[c] !== 'number',
  )

  if (missing.length > 0) {
    return {
      garmentName: garment.name,
      section,
      status: 'warn',
      pieceCount: garment.pieces.length,
      pointsExpected,
      pointsComputed: 0,
      errorCount: 0,
      errors: [],
      missingMeasurements: missing,
      skipped: `${missing.length} misure mancanti nel piano taglie`,
    }
  }

  const result = allGarments
    ? computeWithInheritance(garment, allGarments, measurements, TEST_EASE)
    : computeGarment({ garment, measurements, ease: TEST_EASE })
  const pointsComputed = result.pieces.reduce((s, p) => s + p.points.length, 0)

  let status: GarmentDiagnostic['status']
  if (result.errors.length === 0) status = 'ok'
  else if (pointsExpected > 0 && pointsComputed >= pointsExpected * 0.6) status = 'warn'
  else status = 'error'

  return {
    garmentName: garment.name,
    section,
    status,
    pieceCount: garment.pieces.length,
    pointsExpected,
    pointsComputed,
    errorCount: result.errors.length,
    errors: result.errors.map(e => ({
      piece: e.pieceLabel,
      point: e.pointLabel,
      axis: e.axis,
      formula: e.formula,
      message: e.message,
    })),
    missingMeasurements: [],
  }
}

export interface DiagnosticSummary {
  total: number
  ok: number
  warn: number
  error: number
  results: GarmentDiagnostic[]
}

export function diagnoseAll(
  garments: GarmentContent[],
  book: BookContent,
  useInheritance = true,
): DiagnosticSummary {
  const results = garments.map(g =>
    diagnoseGarment(g, book, useInheritance ? garments : undefined),
  )
  return {
    total: results.length,
    ok: results.filter(r => r.status === 'ok').length,
    warn: results.filter(r => r.status === 'warn').length,
    error: results.filter(r => r.status === 'error').length,
    results,
  }
}
